import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AtivarDoisFatores,
  CodigosRecuperacao,
  ConfigurarDoisFatores,
  DesafioDoisFatores,
  EtapaProgramada,
  Health,
  Login,
  Maquina,
  Me,
  Plano,
  PoliticaSeguranca,
  Reautenticar,
  StatusDoisFatores,
  TrocarSenha,
  Usuario,
  UsuarioNovo,
} from '@pcp/contracts';
import { ApiRequestError, api } from './client';

/*
 * Rotas e campos são os do contrato (docs/04-contrato-api.md): nomes literais do Progress, em português
 * e com hífen, sem tradução. Só os identificadores do código (hooks, variáveis) ficam em inglês.
 */

export const keys = {
  me: ['me'] as const,
  health: ['health'] as const,
  plans: ['planos'] as const,
  plan: (nr: number) => ['planos', nr] as const,
  steps: (nr: number, params: Record<string, string | undefined>) =>
    ['planos', nr, 'etapas', params] as const,
  twoFactor: ['conta', '2fa'] as const,
  policy: ['configuracoes', 'seguranca'] as const,
  users: ['usuarios'] as const,
  machines: ['maquinas'] as const,
};

const noRetryOnClientError = (count: number, error: Error) =>
  !(error instanceof ApiRequestError && error.status >= 400 && error.status < 500) && count < 2;

// ---------- Sessão ----------

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: ({ signal }) => api<Me>('/me', { signal }),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export const isTwoFactorChallenge = (r: Me | DesafioDoisFatores): r is DesafioDoisFatores =>
  'dois-fatores-obrigatorio' in r;

/** Senha correta devolve `Me` (sessão aberta) ou `{ "dois-fatores-obrigatorio": true }` (falta o código). */
export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Login) => api<Me | DesafioDoisFatores>('/auth/login', { method: 'POST', body }),
    onSuccess: (result) => {
      if (isTwoFactorChallenge(result)) return;
      qc.clear();
      qc.setQueryData(keys.me, result);
    },
  });
}

export function useVerifyTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { codigo: string; 'confiar-dispositivo': boolean }) =>
      api<Me>('/auth/2fa/verificar', { method: 'POST', body }),
    onSuccess: (me) => {
      qc.clear();
      qc.setQueryData(keys.me, me);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST' }),
    onSettled: () => qc.clear(),
  });
}

/** Estado do BFF e do Datasul (público). */
export function useHealth() {
  return useQuery({
    queryKey: keys.health,
    queryFn: ({ signal }) => api<Health>('/health', { signal }),
    refetchInterval: 60_000,
    retry: false,
  });
}

// ---------- Planos ----------

export function usePlans(enabled = true) {
  return useQuery({
    queryKey: keys.plans,
    queryFn: ({ signal }) => api<Plano[]>('/planos', { signal }),
    enabled,
    retry: noRetryOnClientError,
  });
}

export function usePlanDetail(nr: number | undefined) {
  return useQuery({
    queryKey: keys.plan(nr ?? 0),
    queryFn: ({ signal }) => api<Plano>(`/planos/${nr}`, { signal }),
    enabled: nr !== undefined,
    retry: noRetryOnClientError,
  });
}

/** Programação de uma máquina/dia. O operador recebe só a própria máquina, hoje e amanhã. */
export function usePlanSteps(
  nr: number | undefined,
  params: { data?: string; maquina?: string } = {},
  options: { refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: keys.steps(nr ?? 0, params),
    queryFn: ({ signal }) => api<EtapaProgramada[]>(`/planos/${nr}/etapas`, { query: params, signal }),
    enabled: nr !== undefined,
    retry: noRetryOnClientError,
    ...(options.refetchInterval ? { refetchInterval: options.refetchInterval } : {}),
  });
}

// ---------- Conta: senha e 2FA ----------

/** Troca da própria senha. As outras sessões da pessoa são encerradas; a atual continua. */
export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TrocarSenha) => api<void>('/conta/senha', { method: 'POST', body }),
    // `troca-senha-obrigatoria` muda no `me`
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
}

export function useTwoFactorStatus() {
  return useQuery({
    queryKey: keys.twoFactor,
    queryFn: ({ signal }) => api<StatusDoisFatores>('/conta/2fa', { signal }),
  });
}

/** Depois de qualquer mudança no 2FA, o `me` (dois-fatores) também muda. */
function useRefreshAccount() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: keys.twoFactor }),
      qc.invalidateQueries({ queryKey: keys.me }),
    ]);
}

export const useSetupTwoFactor = () =>
  useMutation({ mutationFn: () => api<ConfigurarDoisFatores>('/conta/2fa/configurar', { method: 'POST' }) });

export function useEnableTwoFactor() {
  const refresh = useRefreshAccount();
  return useMutation({
    mutationFn: (body: AtivarDoisFatores) =>
      api<CodigosRecuperacao>('/conta/2fa/ativar', { method: 'POST', body }),
    onSuccess: () => refresh(),
  });
}

export function useDisableTwoFactor() {
  const refresh = useRefreshAccount();
  return useMutation({
    mutationFn: (body: Reautenticar) => api<void>('/conta/2fa/desativar', { method: 'POST', body }),
    onSuccess: () => refresh(),
  });
}

export function useRegenerateRecoveryCodes() {
  const refresh = useRefreshAccount();
  return useMutation({
    mutationFn: (body: Reautenticar) =>
      api<CodigosRecuperacao>('/conta/2fa/codigos-recuperacao', { method: 'POST', body }),
    onSuccess: () => refresh(),
  });
}

export function useRevokeTrustedDevices() {
  const refresh = useRefreshAccount();
  return useMutation({
    mutationFn: () => api<void>('/conta/2fa/esquecer-dispositivos', { method: 'POST' }),
    onSuccess: () => refresh(),
  });
}

// ---------- Administração ----------

export function useSecurityPolicy() {
  return useQuery({
    queryKey: keys.policy,
    queryFn: ({ signal }) => api<PoliticaSeguranca>('/configuracoes/seguranca', { signal }),
  });
}

export function useSaveSecurityPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PoliticaSeguranca) =>
      api<PoliticaSeguranca>('/configuracoes/seguranca', { method: 'PUT', body }),
    onSuccess: (data) => qc.setQueryData(keys.policy, data),
  });
}

export function useUsers() {
  return useQuery({
    queryKey: keys.users,
    queryFn: ({ signal }) => api<Usuario[]>('/usuarios', { signal }),
  });
}

export function useMachines(enabled = true) {
  return useQuery({
    queryKey: keys.machines,
    queryFn: ({ signal }) => api<Maquina[]>('/maquinas', { signal }),
    enabled,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UsuarioNovo) => api<Usuario>('/usuarios', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }),
  });
}

export function useResetUserTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<void>(`/usuarios/${id}/2fa/redefinir`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }),
  });
}

/** Admin define uma senha provisória para outra pessoa; ela é obrigada a trocar no próximo acesso. */
export function useResetUserPassword() {
  return useMutation({
    mutationFn: (v: { id: number; senha: string }) =>
      api<void>(`/usuarios/${v.id}/senha`, { method: 'POST', body: { 'senha-nova': v.senha } }),
  });
}
